import { useEffect, useState } from 'react';
import { useBillingApi } from 'api';
import { SubscriptionType } from 'types/billing';
import LoadingSpinner from 'ui/LoadingSpinner';
import BillingPlanView from './BillingPlan.View';
import BillingSection from '../Billing.Section';
import { useBilling, useQuery, useShopify, useShowSpinner } from 'hooks';
import { useParams } from 'react-router-dom';
import BuildPlanModal from 'components/Billing/Stripe/Subscriptions/BuildPlanModal/BuildPlanModal.Container';
import { BrandIntegrationStatus } from 'constants/integrations';
import ShopifyRequiredModal from 'ui/Modals/ShopifyRequiredModal';
import CancelPlanModal from 'components/Billing/Stripe/Subscriptions/CancelPlanModal';
import { Text } from '@innovationdepartment/proxima-ui';
import { useBillingCurrentPlan } from 'components/Billing/useBillingCurrentPlan';
import * as datefns from 'date-fns';
import { SubscriptionStatus } from '@innovationdepartment/proxima-sdk-axios';

const BillingPlan = () => {
  const { brandId: paramBrandId } = useParams();
  const { getShopifyStatus } = useShopify();
  const { /* deleteSubscription, */ loading: navigating } = useBilling();
  const { getBrandSubscription, getShopifySubscription, userChargesAccepted, loading } =
    useBillingApi();
  const { refreshBrandData, loading: brandDataLoading } = useBillingApi();
  const { getQueryParams } = useQuery();

  const { charge_id: chargeId } = getQueryParams<'charge_id'>();

  const [subscription, setSubscription] = useState<SubscriptionType>({
    trialEnd: '',
    status: '' as any,
    paymentFailed: false,
    plan: undefined,
    billApprovalDate: undefined,
    subscriptionStatus: undefined,
  });

  const billingPlan = useBillingCurrentPlan(subscription);

  const [chooseOpen, setChooseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  const fetchSubscription = async (brandId: string) => {
    if (chargeId) {
      await userChargesAccepted(brandId);
      await refreshBrandData(); // refresh brand data to ensure the latest subscription info
    }

    const [billingResponse, shopifySubscription] = await Promise.all([
      getBrandSubscription(brandId),
      getShopifySubscription(brandId),
    ]);

    if (billingResponse.error) return;

    const { plan, status, trialEnd } = billingResponse.data;

    const subscriptionData: SubscriptionType = {
      trialEnd,
      status,
      plan,
      paymentFailed: false,
    };

    if (shopifySubscription) {
      subscriptionData.billApprovalDate = shopifySubscription.chargesAcceptedAt;
      subscriptionData.subscriptionStatus = shopifySubscription.status;
      // not needed, but keeping for backard compatibility with old code
      subscriptionData.paymentFailed =
        shopifySubscription.status in
        {
          [SubscriptionStatus.Declined]: false,
          [SubscriptionStatus.Expired]: false,
          [SubscriptionStatus.Frozen]: false,
        };
    }

    setSubscription(subscriptionData);
  };

  const isShopifyConnected = getShopifyStatus() === BrandIntegrationStatus.Connected;

  const handleButtonClick = (isCancel = false) => {
    if (!isShopifyConnected) {
      setConnectOpen(true);
      return;
    }

    if (isCancel) setCancelOpen(true);
    else setChooseOpen(true);
  };

  const onCancel = async () => {
    // await deleteSubscription();
    setSubscription({ ...subscription, subscriptionStatus: SubscriptionStatus.CancelPending });
    setCancelOpen(false);
  };

  useShowSpinner({ show: brandDataLoading });

  useEffect(() => {
    const fetch = async () => {
      if (!paramBrandId) return;
      // Always fetch once on mount / paramBrandId change
      await fetchSubscription(paramBrandId);
    };

    fetch();
  }, [paramBrandId, navigating]);

  if (loading || navigating) return <LoadingSpinner />;

  const { isCancelled, isTrial, remainingDays, isAccountStillActive, isLocked } = billingPlan;

  let title = <Text>Your current plan</Text>;

  if (isTrial) {
    title = (
      <Text>
        Your <b>Trial</b> is active
      </Text>
    );
  }

  if (isCancelled) {
    title = <Text>Your subscription has been cancelled</Text>;
  }

  if (isLocked) {
    title = (
      <Text>
        Your <b>account</b> is inactive
      </Text>
    );
  }

  const cancellationMessage =
    isCancelled && remainingDays !== undefined
      ? `Access will end ${datefns.format(
          datefns.addDays(new Date(), remainingDays),
          'MMMM d, yyyy'
        )}.`
      : undefined;

  return (
    <BillingSection title={title} isActive={isAccountStillActive}>
      <BillingPlanView
        undertext={cancellationMessage}
        billingPlanStatus={billingPlan}
        handleButtonClick={handleButtonClick}
      />

      <ShopifyRequiredModal open={connectOpen} onClose={() => setConnectOpen(false)} />

      <BuildPlanModal
        open={chooseOpen}
        onClose={() => setChooseOpen(false)}
        subscription={subscription}
      />
      <CancelPlanModal open={cancelOpen} onClose={() => setCancelOpen(false)} onAccept={onCancel} />
    </BillingSection>
  );
};

export default BillingPlan;
